import history from "../uidispatcherapi/BettyHistoryModule";
import { setCurrentPathname } from "../utils/locationUtil";

function getRedirectParam(path) {
	if (typeof path === "string" && path.length > 0) {
		if (path.startsWith("/")) {
			path = path.substring(1);
		}

		return encodeURIComponent(path);
	} else {
		return "";
	}
}

export function cleanUpUrlForRedirect(uncleandUrl: string): string {
	// for local testing add betty-pp -> localhost to /etc/hosts and have unifiedapi on port 80
	return uncleandUrl.replace("/testLoginIdam", "/exlogin").replace(/^http:/, "https:");
}

/** Result of a hook that basically says the hook did nothing */
export const HOOK_SKIPPED = {
	checkNext: true,
	didNavigate: false,
	didRedirect: false,
};

/** Result of a hook that navigates via window.location */
export const HOOK_NAVIGATED = {
	checkNext: false,
	didNavigate: true,
	didRedirect: false,
};

/** Result of a hook that redirects via react-router */
export const HOOK_REDIRECTED = {
	checkNext: false,
	didNavigate: false,
	didRedirect: true,
};

/**
 Make sure that, if errorPage is called no other RouteHandler is called.
 */
export const errorHook = function (state) {
	if (state.path?.startsWith("/error")) {
		return {
			checkNext: false,
			didNavigate: false,
			didRedirect: false,
		};
	}

	return HOOK_SKIPPED;
};

const isShopUrl = function (pathname: string) {
	return !history.isBackend(pathname);
};

const isBackendUrl = function (pathname: string) {
	return history.isBackend(pathname);
};

const toBackend = function (pathname: string) {
	return history.toBackend(pathname);
};

const toShop = function (pathname: string) {
	return history.toShop(pathname);
};

const isLoginUrl = function (pathname: string) {
	return pathname.includes("/login") || pathname.includes("/exlogin");
};

/**
 * Force employees in the shop to use the backend.
 */
export const employeesMustUseBackend = function (state, SessionStore) {
	/**
	 * If we are in the shop and logged in as employee, we need to redirect to the backend:
	 */
	const isEmployee = SessionStore.isEmployee();
	const isShop = isShopUrl(window.location.pathname);

	if (isShop && isEmployee && !SessionStore.pending()) {
		// redirect employees visiting /shop to the equivalent /backend URL
		setCurrentPathname(toBackend(window.location.pathname));
		return HOOK_NAVIGATED;
	} else {
		return HOOK_SKIPPED;
	}
};

/**
 * Force employees in the backend to use the shop.
 */
export const customersMustUseShop = function (state, SessionStore) {
	/**
	 * If we are in the backend and logged in as customer, we need to redirect to the shop:
	 */
	const isCustomer = !SessionStore.isEmployee(); // Anonymous customers are also customers

	const isBackend = isBackendUrl(window.location.pathname);
	const isLogin = isLoginUrl(window.location.pathname);

	if (isBackend && isCustomer && !isLogin && !SessionStore.pending()) {
		setCurrentPathname(toShop(window.location.pathname));
		return HOOK_NAVIGATED;
	} else {
		return HOOK_SKIPPED;
	}
};
// Ensure that an employee selects a customer.
export const customerSelectionHook = function (state, SessionStore, Handler) {
	const correctedPath = cleanUpUrlForRedirect(state.path);
	const forceSelection = state.query?.setCustomerId;

	if (!SessionStore.loggedIn() || SessionStore.pending()) {
		return HOOK_SKIPPED;
	}

	const customerSelectionNeccessary = !SessionStore.getCustomerId() && !state.routeParams.noCustomerNeed;
	const isCustomerSearchPage = correctedPath.startsWith("/backoffice/customer/search");

	if ((customerSelectionNeccessary || forceSelection) && !isCustomerSearchPage) {
		const redirectTo = "/backoffice/customer/search/" + getRedirectParam(correctedPath);
		Handler.transitionTo(redirectTo, state.query);
		return HOOK_REDIRECTED;
	}

	if (!SessionStore.getCustomerId() && isCustomerSearchPage) {
		return {
			checkNext: false,
			didRedirect: false,
		};
	}

	return HOOK_SKIPPED;
};
